/* Ticketing, as the design draws it: one line saying whether incidents reach a service desk, the
 * service desks in a card, the routing rules in another, and two short dialogs.
 *
 * **The headline is derived from what is absent as well as what exists.** A tenant can hold correct
 * rules and still raise no ticket for a contract breach, so the line under the heading names the
 * incident types a critical incident would reach nobody for — the one statement on the screen no
 * row can make.
 *
 * **A credential is typed once and never shown again.** It is stored in Vault and no response
 * carries it, so the connect dialog is the only moment it exists in the browser; the flash after a
 * connection says so.
 */

import type { FormEvent, ReactElement } from 'react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  IncidentKind,
  ItsmConnection,
  ItsmProvider,
  Severity,
} from '@/shared/api/generated/itsm';
import { ageText } from '@/shared/model/age';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import {
  useConnections,
  useDeclareTicketRule,
  useDeleteConnection,
  useProviders,
  useRegisterConnection,
  useSetConnectionEnabled,
  useTicketPolicy,
  useVerifyConnection,
  useWithdrawTicketRule,
} from '../api/itsm';
import {
  destinationWarningOf,
  inService,
  reachOf,
  stateTone,
  summarise,
  uncoveredKinds,
} from '../model/integrations';
import { PROVIDERS, PROVIDER_ORDER, profileFor } from '../model/providers';

const KINDS = [
  'schema_drift',
  'freshness_breach',
  'volume_anomaly',
  'assessment_overdue',
  'contract_breach',
  'reconciliation_break',
  'rule_persistent_failure',
  'harvest_schedule_failing',
  'execution_schedule_failing',
  'reconciliation_schedule_failing',
] as const satisfies readonly IncidentKind[];

const SEVERITIES = ['info', 'warning', 'critical'] as const satisfies readonly Severity[];

/** The host of a desk's address, for a ServiceNow row that has no project to name. */
function hostOf(address: string): string {
  try {
    return new URL(address).host;
  } catch {
    return address;
  }
}

interface Flash {
  readonly title: string;
  readonly body: string;
}

export function IntegrationsScreen(): ReactElement {
  const { t } = useTranslation();
  const connections = useConnections();
  const policy = useTicketPolicy();

  const verify = useVerifyConnection();
  const setEnabled = useSetConnectionEnabled();
  const remove = useDeleteConnection();
  const withdraw = useWithdrawTicketRule();

  const [connecting, setConnecting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [flash, setFlash] = useState<Flash | null>(null);
  const [now] = useState(() => new Date());

  const desks = connections.data?.connections ?? [];
  const rules = policy.data?.rules ?? [];
  const declared = policy.data?.declared ?? false;
  const uncovered =
    policy.data === undefined ? [] : uncoveredKinds(policy.data, desks, KINDS);
  const summary = summarise({ declared, rules }, desks);
  const liveRules = rules.filter((rule) => reachOf(rule, desks) === 'files').length;

  const busyDesk =
    verify.isPending || setEnabled.isPending || remove.isPending
      ? (verify.variables ??
        setEnabled.variables?.connectionId ??
        remove.variables ??
        null)
      : null;
  const failure =
    verify.error ?? setEnabled.error ?? remove.error ?? withdraw.error ?? null;
  const loading = connections.isPending || policy.isPending;
  const unreadable = connections.isError || policy.isError;

  return (
    <div className="lq-tk">
      <PageHeader
        title={t('itsm.title')}
        lede={t('itsm.subtitle')}
        actions={
          <span className="lq-tk__headActions">
            {loading || unreadable ? null : (
              /* Counts, never a rate: no endpoint reports tickets raised this month, and a figure
                 invented to fill the space is the defect this product exists to refuse. */
              <span className="lq-tk__meta">
                {t('itsm.meta', {
                  desks: desks.length,
                  live: liveRules,
                  uncovered: uncovered.length,
                })}
              </span>
            )}
            <Button
              tone="primary"
              size="compact"
              onClick={() => {
                setConnecting(true);
              }}
            >
              {t('itsm.configure')}
            </Button>
          </span>
        }
      />

      {flash !== null && (
        <div className="lq-tk__flash" role="status">
          <p className="lq-tk__flashText">
            <strong>{flash.title}</strong> {flash.body}
          </p>
          <button
            type="button"
            className="lq-tk__flashClose"
            aria-label={t('common.dismiss')}
            onClick={() => {
              setFlash(null);
            }}
          >
            ×
          </button>
        </div>
      )}

      {unreadable ? (
        /* A read that failed says nothing about the configuration, so the screen does not either. */
        <p className="lq-tk__error" role="alert">
          <strong>{t('itsm.unreadable')}</strong>{' '}
          {(connections.error ?? policy.error)?.message ?? ''}
        </p>
      ) : loading ? (
        <SkeletonRows rows={4} />
      ) : (
        <>
          {/* Announced politely: this changes as rules and desks change, and an assertive region
              would interrupt whatever a screen reader user was doing. */}
          <div role="status">
            <div className="lq-tk__head">{t(`itsm.headline.${summary.state}`)}</div>
            {uncovered.length === 0 ? null : (
              <p className="lq-tk__miss">
                {t('itsm.headline.uncovered', {
                  kinds: uncovered.map((kind) => t(`itsm.kindLower.${kind}`)).join(', '),
                })}
              </p>
            )}
          </div>

          {failure === null ? null : (
            <p className="lq-tk__error" role="alert">
              {failure.message}
            </p>
          )}

          <section className="lq-card lq-tk__card" aria-labelledby="lq-tk-desks">
            <div className="lq-tk__cardHead">
              <div>
                <h2 id="lq-tk-desks" className="lq-tk__cardTitle">
                  {t('itsm.desks.title')}
                </h2>
                <p className="lq-tk__cardSub">{t('itsm.desks.lede')}</p>
              </div>
            </div>
            <table
              className="lq-tk__table lq-tk__table--desks"
              aria-labelledby="lq-tk-desks"
            >
              <thead>
                <tr>
                  <th scope="col">{t('itsm.column.destination')}</th>
                  <th scope="col">{t('itsm.column.state')}</th>
                  <th scope="col">{t('itsm.column.lastChecked')}</th>
                  <th scope="col">
                    <span className="lq-visually-hidden">{t('itsm.column.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {desks.map((desk) => (
                  <DeskRow
                    key={desk.id}
                    desk={desk}
                    now={now}
                    busy={busyDesk === desk.id}
                    onVerify={() => {
                      verify.mutate(desk.id);
                    }}
                    onToggle={() => {
                      setEnabled.mutate({
                        connectionId: desk.id,
                        enabled: !inService(desk),
                      });
                    }}
                    onRemove={() => {
                      remove.mutate(desk.id);
                    }}
                  />
                ))}
              </tbody>
            </table>
            {desks.length === 0 ? (
              <p className="lq-tk__none">{t('itsm.desks.none')}</p>
            ) : null}
          </section>

          <section className="lq-card lq-tk__card" aria-labelledby="lq-tk-rules">
            <div className="lq-tk__cardHead">
              <div className="lq-tk__grow">
                <h2 id="lq-tk-rules" className="lq-tk__cardTitle">
                  {t('itsm.rules.title')}
                </h2>
                <p className="lq-tk__cardSub">{t('itsm.rules.lede')}</p>
              </div>
              <Button
                size="compact"
                disabled={!desks.some(inService)}
                title={desks.some(inService) ? undefined : t('itsm.rules.needsDesk')}
                onClick={() => {
                  setAdding(true);
                }}
              >
                {t('itsm.addRule')}
              </Button>
            </div>
            <table
              className="lq-tk__table lq-tk__table--rules"
              aria-labelledby="lq-tk-rules"
            >
              <thead>
                <tr>
                  <th scope="col">{t('itsm.column.kind')}</th>
                  <th scope="col">{t('itsm.column.severity')}</th>
                  <th scope="col">{t('itsm.column.destination')}</th>
                  <th scope="col">{t('itsm.column.ticket')}</th>
                  <th scope="col">
                    <span className="lq-visually-hidden">{t('itsm.column.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => {
                  const reach = reachOf(rule, desks);
                  const doubt = destinationWarningOf(rule, desks);
                  return (
                    <tr key={rule.id}>
                      <td data-label={t('itsm.column.kind')}>
                        {/* Null is every kind, not "unknown" and not "none". */}
                        {rule.incident_kind === null
                          ? t('itsm.kind.any')
                          : t(`itsm.kind.${rule.incident_kind}`)}
                      </td>
                      <td data-label={t('itsm.column.severity')} className="lq-tk__dim">
                        {t(`itsm.severityFloor.${rule.min_severity}`)}
                      </td>
                      <td data-label={t('itsm.column.destination')}>
                        {/* Resolved by the server. A bare UUID here is the defect this codebase
                            has shipped four times. */}
                        {rule.connection_name}
                      </td>
                      <td
                        data-label={t('itsm.column.ticket')}
                        className="lq-tk__verdict"
                        data-tone={
                          reach === 'files' ? (doubt === null ? 'ok' : 'warn') : 'warn'
                        }
                        title={
                          doubt === null
                            ? t(`itsm.reachMeaning.${reach}`)
                            : t(`itsm.flowDoubtMeaning.${doubt}`)
                        }
                      >
                        {t(`itsm.reach.${reach}`)}
                        {doubt === null ? null : ` · ${t(`itsm.flowDoubt.${doubt}`)}`}
                      </td>
                      <td className="lq-tk__actions">
                        <Button
                          size="compact"
                          busy={withdraw.isPending && withdraw.variables === rule.id}
                          title={t('itsm.withdrawMeaning')}
                          onClick={() => {
                            withdraw.mutate(rule.id);
                          }}
                        >
                          {t('itsm.remove')}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {rules.length === 0 ? (
              <p className="lq-tk__none">{t('itsm.rules.none')}</p>
            ) : null}
          </section>
        </>
      )}

      <ConnectModal
        open={connecting}
        onClose={() => {
          setConnecting(false);
        }}
        onConnected={() => {
          setConnecting(false);
          setFlash({ title: t('itsm.connected.title'), body: t('itsm.connected.body') });
        }}
      />
      <RuleModal
        open={adding}
        connections={desks}
        onClose={() => {
          setAdding(false);
        }}
      />
    </div>
  );
}

function DeskRow({
  desk,
  now,
  busy,
  onVerify,
  onToggle,
  onRemove,
}: {
  readonly desk: ItsmConnection;
  readonly now: Date;
  readonly busy: boolean;
  readonly onVerify: () => void;
  readonly onToggle: () => void;
  readonly onRemove: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const live = inService(desk);
  const tone =
    stateTone(desk.state) === 'ok'
      ? 'ok'
      : desk.state === 'unreachable'
        ? 'warn'
        : 'muted';

  return (
    <tr>
      <td data-label={t('itsm.column.destination')}>
        <div className="lq-tk__desk">
          <span className="lq-tk__prov">{PROVIDERS[desk.provider].label}</span>
          <span className="lq-tk__deskName">{desk.name}</span>
        </div>
        <div className="lq-tk__dim" title={desk.instance_url}>
          {/* Where the ticket lands, for a provider that has somewhere to put it; otherwise where
              Lumiq signs in. */}
          {desk.project_key == null
            ? hostOf(desk.instance_url)
            : t('itsm.destinationFact', {
                project: desk.project_key,
                issueType: desk.issue_type ?? '',
              })}
        </div>
      </td>
      <td data-label={t('itsm.column.state')}>
        <span
          className="lq-tk__state"
          data-tone={tone}
          data-hollow={desk.last_checked_at === null && live ? 'true' : undefined}
          title={
            desk.last_check_detail === null
              ? undefined
              : t(`itsm.checkDetail.${desk.last_check_detail}`, {
                  defaultValue: desk.last_check_detail,
                })
          }
        >
          <span className="lq-tk__dot" aria-hidden="true" />
          {t(`itsm.state.${desk.state}`)}
        </span>
      </td>
      <td data-label={t('itsm.column.lastChecked')} className="lq-tk__dim">
        {/* Never checked is a different fact from checked-and-failed, so it is a word rather than
            a dash. */}
        {desk.last_checked_at === null ? (
          t('itsm.neverChecked')
        ) : (
          <span title={new Date(desk.last_checked_at).toLocaleString()}>
            {ageText(t, desk.last_checked_at, now) ?? desk.last_checked_at}
          </span>
        )}
      </td>
      <td className="lq-tk__actions">
        <Button size="compact" disabled={busy} onClick={onVerify}>
          {busy ? t('itsm.checking') : t('itsm.check')}
        </Button>
        <Button
          size="compact"
          disabled={busy}
          title={t(live ? 'itsm.disableMeaning' : 'itsm.enableMeaning')}
          onClick={onToggle}
        >
          {t(live ? 'itsm.disable' : 'itsm.enable')}
        </Button>
        <Button
          size="compact"
          className="lq-tk__danger"
          disabled={busy}
          title={t('itsm.removeMeaning')}
          onClick={onRemove}
        >
          {t('itsm.remove')}
        </Button>
      </td>
    </tr>
  );
}

interface ConnectDraft {
  readonly name: string;
  readonly instanceUrl: string;
  readonly account: string;
  readonly secret: string;
  readonly projectKey: string;
  readonly issueType: string;
}

const BLANK: ConnectDraft = {
  name: '',
  instanceUrl: '',
  account: '',
  secret: '',
  projectKey: '',
  issueType: '',
};

function ConnectModal({
  open,
  onClose,
  onConnected,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onConnected: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const providers = useProviders();
  const register = useRegisterConnection();
  const [chosen, setChosen] = useState<ItsmProvider | null>(null);
  const [draft, setDraft] = useState<ConnectDraft>(BLANK);
  const addressHint = useId();

  /* Reset on close, so a half-typed credential is never shown to whoever opens it next. */
  useEffect(() => {
    if (!open) {
      setChosen(null);
      setDraft(BLANK);
      register.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const profile = chosen === null ? null : profileFor(chosen);
  const set = (key: keyof ConnectDraft, value: string): void => {
    setDraft((current) => ({ ...current, [key]: value }));
  };

  const incomplete =
    draft.name.trim() === '' ||
    draft.instanceUrl.trim() === '' ||
    draft.account.trim() === '' ||
    draft.secret === '' ||
    (profile?.needsDestination === true &&
      (draft.projectKey.trim() === '' || draft.issueType.trim() === ''));

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (chosen === null || profile === null || register.isPending || incomplete) return;
    register.mutate(
      {
        provider: chosen,
        name: draft.name.trim(),
        instance_url: draft.instanceUrl.trim(),
        credentials: {
          auth_type: profile.authType,
          username: draft.account.trim(),
          ...(profile.authType === 'api_token'
            ? { token: draft.secret }
            : { password: draft.secret }),
        },
        ...(profile.needsDestination
          ? { project_key: draft.projectKey.trim(), issue_type: draft.issueType.trim() }
          : {}),
      },
      { onSuccess: onConnected },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="lq-tk__modal"
      title={
        profile === null
          ? t('itsm.connect.choose')
          : t('itsm.connect.to', { provider: profile.label })
      }
      {...(profile === null ? {} : { hint: t('itsm.connect.checks') })}
    >
      {profile === null ? (
        <div className="lq-tk__tiles">
          {PROVIDER_ORDER.map((provider) => {
            const available =
              providers.data?.providers.find((each) => each.provider === provider)
                ?.available ?? true;
            return (
              <button
                key={provider}
                type="button"
                className="lq-tk__tile"
                disabled={!available}
                onClick={() => {
                  setChosen(provider);
                }}
              >
                <span className="lq-tk__tileName">{PROVIDERS[provider].label}</span>
                <span className="lq-tk__dim">
                  {t(`itsm.authType.${PROVIDERS[provider].authType}`)}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <form id="lq-tk-connect" className="lq-tk__form" onSubmit={submit}>
          <label className="lq-tk__field">
            {t('itsm.form.name')}
            <input
              className="lq-input"
              value={draft.name}
              onChange={(event) => {
                set('name', event.target.value);
              }}
            />
          </label>
          <label className="lq-tk__field">
            {t('itsm.form.address')}
            <input
              className="lq-input"
              value={draft.instanceUrl}
              inputMode="url"
              placeholder={profile.addressExample}
              aria-describedby={addressHint}
              onChange={(event) => {
                set('instanceUrl', event.target.value);
              }}
            />
            <span id={addressHint} className="lq-tk__hint">
              {t('itsm.form.addressHint')}
            </span>
          </label>
          <label className="lq-tk__field">
            {t(`itsm.form.account.${profile.authType}`)}
            <input
              className="lq-input"
              value={draft.account}
              onChange={(event) => {
                set('account', event.target.value);
              }}
            />
          </label>
          <label className="lq-tk__field">
            {t(`itsm.form.secret.${profile.authType}`)}
            <input
              className="lq-input"
              type="password"
              autoComplete="new-password"
              spellCheck={false}
              title={profile.credentialHint}
              value={draft.secret}
              onChange={(event) => {
                set('secret', event.target.value);
              }}
            />
          </label>
          {profile.needsDestination ? (
            <div className="lq-tk__pair">
              <label className="lq-tk__field">
                {t('itsm.form.projectKey')}
                <input
                  className="lq-input"
                  value={draft.projectKey}
                  onChange={(event) => {
                    set('projectKey', event.target.value);
                  }}
                />
              </label>
              <label className="lq-tk__field">
                {t('itsm.form.issueType')}
                <input
                  className="lq-input"
                  value={draft.issueType}
                  placeholder="Incident"
                  onChange={(event) => {
                    set('issueType', event.target.value);
                  }}
                />
              </label>
            </div>
          ) : null}
          {register.isError ? (
            <p className="lq-tk__error" role="alert">
              {register.error.message}
            </p>
          ) : null}
        </form>
      )}
      <div className="lq-tk__foot">
        <Button onClick={onClose}>{t('itsm.cancel')}</Button>
        {profile === null ? null : (
          <Button
            tone="primary"
            type="submit"
            form="lq-tk-connect"
            disabled={incomplete}
            busy={register.isPending}
          >
            {t('itsm.connectAndCheck')}
          </Button>
        )}
      </div>
    </Modal>
  );
}

function RuleModal({
  open,
  connections,
  onClose,
}: {
  readonly open: boolean;
  readonly connections: readonly ItsmConnection[];
  readonly onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const declare = useDeclareTicketRule();
  const usable = connections.filter(inService);
  const [kind, setKind] = useState<string>('');
  const [severity, setSeverity] = useState<Severity>('critical');
  const [destination, setDestination] = useState<string>('');
  const severityHint = useId();
  const destinationHint = useId();

  useEffect(() => {
    if (!open) {
      setKind('');
      setSeverity('critical');
      declare.reset();
    }
    /* Pre-selected when there is one usable desk — a blank select is a required field whose only
       answer is already known. */
    if (open) setDestination(usable[0]?.id ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (destination === '' || declare.isPending) return;
    declare.mutate(
      {
        connection_id: destination,
        incident_kind: kind === '' ? null : (kind as IncidentKind),
        min_severity: severity,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="lq-tk__modal lq-tk__modal--narrow"
      title={t('itsm.rule.title')}
      hint={t('itsm.rule.hint')}
    >
      <form id="lq-tk-rule" className="lq-tk__form" onSubmit={submit}>
        <label className="lq-tk__field">
          {t('itsm.field.kind')}
          <select
            className="lq-input"
            value={kind}
            onChange={(event) => {
              setKind(event.target.value);
            }}
          >
            <option value="">{t('itsm.kind.any')}</option>
            {KINDS.map((each) => (
              <option key={each} value={each}>
                {t(`itsm.kind.${each}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="lq-tk__field">
          {t('itsm.field.severity')}
          <select
            className="lq-input"
            value={severity}
            aria-describedby={severityHint}
            onChange={(event) => {
              setSeverity(event.target.value as Severity);
            }}
          >
            {SEVERITIES.map((each) => (
              <option key={each} value={each}>
                {t(`itsm.severityFloor.${each}`)}
              </option>
            ))}
          </select>
          <span id={severityHint} className="lq-tk__hint">
            {t('itsm.field.severityHint')}
          </span>
        </label>
        <label className="lq-tk__field">
          {t('itsm.field.destination')}
          <select
            className="lq-input"
            value={destination}
            aria-describedby={destinationHint}
            onChange={(event) => {
              setDestination(event.target.value);
            }}
          >
            {usable.map((connection) => (
              <option key={connection.id} value={connection.id}>
                {connection.name}
              </option>
            ))}
          </select>
          {/* Only desks in service are offered: a rule pointing at a switched-off desk raises
              nothing, and offering one would help somebody build the pairing this screen warns
              about. */}
          <span id={destinationHint} className="lq-tk__hint">
            {t('itsm.field.destinationHint')}
          </span>
        </label>
        {declare.isError ? (
          <p className="lq-tk__error" role="alert">
            {declare.error.message}
          </p>
        ) : null}
      </form>
      <div className="lq-tk__foot">
        <Button onClick={onClose}>{t('itsm.cancel')}</Button>
        <Button
          tone="primary"
          type="submit"
          form="lq-tk-rule"
          disabled={destination === ''}
          busy={declare.isPending}
        >
          {t('itsm.addRule')}
        </Button>
      </div>
    </Modal>
  );
}
